import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import SpotlightCard from "@/components/SpotlightCard";
import { PROGRAMS, IMAGES } from "@/data/content";

const Programs = () => (
  <section
    id="programs"
    data-testid="programs-section"
    className="relative border-t border-[#27272A] bg-[#0a0a0c] py-24 sm:py-32"
  >
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHead
          eyebrow="The Two Pillars"
          title="Two protocols. Run together, they rebuild the whole machine."
          testId="programs-heading"
        />
        <Reveal delay={0.1}>
          <Link
            data-testid="programs-method-link"
            to="/method"
            className="group flex items-center gap-2 font-jb text-xs uppercase tracking-[0.22em] text-[#CC0A14] transition-colors duration-300 hover:text-white"
          >
            Full method breakdown
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {PROGRAMS.map((p, i) => (
          <Reveal
            key={p.id}
            delay={i * 0.12}
            className={i === 0 ? "lg:col-span-7" : "lg:col-span-5"}
          >
            <SpotlightCard testId={`program-card-${p.id}`} className="h-full">
              <div className="flex h-full flex-col">
                <div className="relative h-56 overflow-hidden sm:h-64">
                  <img
                    src={IMAGES[p.image]}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-[#121214]/30 to-transparent" />
                  <span className="absolute left-5 top-5 border border-[#CC0A14]/60 bg-[#080808]/80 px-3 py-1.5 font-jb text-[10px] uppercase tracking-[0.25em] text-[#CC0A14] backdrop-blur-sm">
                    {p.tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7 sm:p-9">
                  <p className="font-jb text-[11px] uppercase tracking-[0.25em] text-zinc-600">
                    {p.index}
                  </p>
                  <h3 className="mt-3 font-syne text-xl font-bold tracking-tight text-white sm:text-2xl">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{p.description}</p>
                  <ul className="mt-6 space-y-3">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3 text-sm text-zinc-300">
                        <Check size={15} className="mt-0.5 shrink-0 text-[#CC0A14]" />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </SpotlightCard>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-6 grid grid-cols-2 border border-[#27272A] bg-[#121214] sm:grid-cols-4">
          {[
            ["20–40", "minutes daily chew volume"],
            ["08", "industrial oils eliminated"],
            ["04wk", "photo check-in cadence"],
            ["05", "max ingredients on a label"],
          ].map(([v, l]) => (
            <div
              key={l}
              className="border-[#27272A] p-6 [&:nth-child(odd)]:border-r sm:[&:not(:last-child)]:border-r"
            >
              <p className="font-syne text-3xl font-extrabold text-[#CC0A14]">{v}</p>
              <p className="mt-2 font-jb text-[10px] uppercase leading-relaxed tracking-[0.18em] text-zinc-500">
                {l}
              </p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export default Programs;
